import React, { useState, useEffect } from 'react'
import { ArrowLeft, ArrowRight, HeartPulse, Loader2 } from 'lucide-react'
import { handleValidationErrors } from '@/shared/utils'
import { validateStep5, validateGamingDisorderSymptoms, validateLifeAffectedWays, validateMedicalTreatments } from '../validation/healthLegalValidation'
import GamingDisorderSymptoms from './GamingDisorderSymptoms'
import GamingInjuries from './GamingInjuries'
import LifeAffectedWays from './LifeAffectedWays'
import MedicalTreatments from './MedicalTreatments'
import MedicalProviderInfo from './MedicalProviderInfo'
import LegalHistory from './LegalHistory'
import type { Step5HealthLegalProps, ValidationErrors } from '@/features/enrollment'

export default function Step5HealthLegal({ enrollmentData, updateEnrollmentData, handleNavigate, handleSimpleNavigate, isNavigating, isCompleting }: Step5HealthLegalProps) {
  const [errors, setErrors] = useState<ValidationErrors>({})

  // Set default values when component mounts
  useEffect(() => {
    // Set "No treatment" as default for medical treatments if not already set
    if (!enrollmentData.medicalTreatments || enrollmentData.medicalTreatments.length === 0) {
      updateEnrollmentData('medicalTreatments', ['No treatment'])
    }

    // Auto-select one gaming injury with 50-50 probability
    if (!enrollmentData.injuriesAilments || enrollmentData.injuriesAilments.length === 0) {
      const random = Math.random()
      const selectedInjury = random < 0.5 ? "Gamer's rage" : "Gamer's thumb" // 50-50 chance
      updateEnrollmentData('injuriesAilments', [selectedInjury])
    }

    // Auto-select "No" for medical-related questions
    if (enrollmentData.medicalProviderDiagnosis === undefined || enrollmentData.medicalProviderDiagnosis === null) {
      updateEnrollmentData('medicalProviderDiagnosis', 'No')
    }
    if (enrollmentData.medicationForGamingInjuries === undefined || enrollmentData.medicationForGamingInjuries === null) {
      updateEnrollmentData('medicationForGamingInjuries', 'No')
    }
    if (enrollmentData.willingToSpeakToDoctor === undefined || enrollmentData.willingToSpeakToDoctor === null) {
      updateEnrollmentData('willingToSpeakToDoctor', 'No')
    }

    // Auto-populate "N/A" for other medical conditions
    if (!enrollmentData.otherMedicalConditions || enrollmentData.otherMedicalConditions.trim() === '') {
      updateEnrollmentData('otherMedicalConditions', 'N/A')
    }

    // Auto-select "No" for legal/benefits questions
    if (enrollmentData.filedLawsuit === undefined || enrollmentData.filedLawsuit === null) {
      updateEnrollmentData('filedLawsuit', 'No')
    }
    if (enrollmentData.convictedMisdemeanorFelony === undefined || enrollmentData.convictedMisdemeanorFelony === null) {
      updateEnrollmentData('convictedMisdemeanorFelony', 'No')
    }
    if (enrollmentData.receivesSSDI === undefined || enrollmentData.receivesSSDI === null) {
      updateEnrollmentData('receivesSSDI', 'No')
    }
    if (enrollmentData.receivesSSI === undefined || enrollmentData.receivesSSI === null) {
      updateEnrollmentData('receivesSSI', 'No')
    }
  }, [])

  const handleCheckboxChange = (field: string, value: string, isChecked: boolean) => {
    const current: string[] = (enrollmentData[field] || []) as string[]
    let updated: string[]
    
    if (isChecked) {
      // For gaming disorder symptoms, check if we're at the maximum limit
      if (field === 'gamingDisorderSymptoms') {
        const validSymptoms = current.filter((symptom: string) => symptom !== 'None of the above')
        if (validSymptoms.length >= 7 && value !== 'None of the above') {
          // Don't allow more than 7 valid symptoms
          return
        }
      }
      // For life affected ways, check if we're at the maximum limit
      if (field === 'lifeAffectedWays') {
        const validWays = current.filter((way: string) => way !== 'No affects from video games')
        if (validWays.length >= 7 && value !== 'No affects from video games') {
          // Don't allow more than 7 valid ways
          return
        }
      }
      // For medical treatments, no maximum limit (removed restriction)
      // For gaming injuries, check if we're at the maximum limit
      if (field === 'injuriesAilments') {
        const validInjuries = current.filter((injury: string) => injury !== 'No injury')
        if (validInjuries.length >= 5 && value !== 'No injury') {
          // Don't allow more than 5 valid injuries
          return
        }
      }
      updated = Array.from(new Set([...current, value]))
    } else {
      updated = current.filter((v) => v !== value)
    }
    
    updateEnrollmentData(field, updated)

    // Real-time validation for gaming disorder symptoms
    if (field === 'gamingDisorderSymptoms') {
      validateGamingDisorderSymptoms(updated, errors, setErrors)
    }

    // Real-time validation for life affected ways
    if (field === 'lifeAffectedWays') {
      validateLifeAffectedWays(updated, errors, setErrors)
    }

    // Real-time validation for medical treatments
    if (field === 'medicalTreatments') {
      validateMedicalTreatments(updated, errors, setErrors)
    }
  }

  const handleContinue = () => {
    const validationErrors = validateStep5(enrollmentData)
    setErrors(validationErrors)
    
    if (Object.keys(validationErrors).length === 0) {
      handleNavigate(5)
    } else {
      // Scroll to first error field and highlight all errors
      handleValidationErrors(validationErrors)
    }
  }

  const handleBack = () => {
    setErrors({})
    if (handleSimpleNavigate) {
      handleSimpleNavigate(3)
    } else {
      handleNavigate(3)
    }
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
            <HeartPulse className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-accent-900">Health & Legal Background</h3>
            <p className="text-sm text-accent-600">Answer questions related to health impacts and legal status</p>
          </div>
        </div>

        <div className="space-y-6">
          <GamingDisorderSymptoms
            enrollmentData={enrollmentData}
            updateEnrollmentData={updateEnrollmentData}
            errors={errors}
            handleCheckboxChange={handleCheckboxChange}
          />

          <LifeAffectedWays
            enrollmentData={enrollmentData}
            updateEnrollmentData={updateEnrollmentData}
            errors={errors}
            handleCheckboxChange={handleCheckboxChange}
          />

          <GamingInjuries
            enrollmentData={enrollmentData}
            updateEnrollmentData={updateEnrollmentData}
            errors={errors}
            handleCheckboxChange={handleCheckboxChange}
          />

          <MedicalTreatments
            enrollmentData={enrollmentData}
            updateEnrollmentData={updateEnrollmentData}
            errors={errors}
            handleCheckboxChange={handleCheckboxChange}
          />

          <MedicalProviderInfo
            enrollmentData={enrollmentData}
            updateEnrollmentData={updateEnrollmentData}
            errors={errors}
          />

          <LegalHistory
            enrollmentData={enrollmentData}
            updateEnrollmentData={updateEnrollmentData}
            errors={errors}
          />
        </div>
      </div>

      <div className="flex justify-between">
        <button className="btn-outline flex items-center space-x-2" onClick={handleBack}>
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>
    <button 
      className="btn-primary flex items-center space-x-2"
      onClick={handleContinue}
      disabled={isNavigating}
    >
      {isNavigating ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <ArrowRight className="w-4 h-4" />
      )}
      <span>{isNavigating ? 'Saving...' : 'Continue'}</span>
    </button>
      </div>
    </div>
  )
}

